import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { SubscriptionCadence } from "components/Conversations/Views/Drawer/components/Subscriptions/helpers";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxChip from "components/UI/AcxChip";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import AcxToggleButton from "components/UI/AcxToggleButton";
import { observer } from "mobx-react";
import moment from "moment";
import React from "react";
import {
    KboFlowSubscription,
    KboFlowSubscriptionsService,
} from "services/KboFlowSubscriptionsService";
import { UserForSharing, UserService } from "services/UserService";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";

type Props = {
    /** The admin route supplies this explicitly; KBo Settings uses the selected tenant. */
    organizationId?: string;
    breadcrumbs?: React.ReactNode;
};

type SubscriptionRow = KboFlowSubscription & {
    displayName: string;
    cadenceLabel: string;
};

type UserOption = UserForSharing & {
    label: string;
};

const cadenceLabels: Record<SubscriptionCadence, string> = {
    [SubscriptionCadence.Daily]: "Daily",
    [SubscriptionCadence.Weekly]: "Weekly",
    [SubscriptionCadence.Monthly]: "Monthly",
};

const cadenceOptions = [
    {
        id: "kbo-subscription-cadence-weekly",
        value: SubscriptionCadence.Weekly,
        element: "Weekly",
    },
    {
        id: "kbo-subscription-cadence-monthly",
        value: SubscriptionCadence.Monthly,
        element: "Monthly",
    },
    {
        id: "kbo-subscription-cadence-daily",
        value: SubscriptionCadence.Daily,
        element: "Daily",
    },
];

const formatDateTime = (value?: string | null) =>
    value ? moment(value).format("MMM D, YYYY h:mm A") : "—";

const getDisplayName = (subscription: KboFlowSubscription) =>
    `${subscription.firstName ?? ""} ${subscription.lastName ?? ""}`.trim() ||
    subscription.userName ||
    subscription.email ||
    subscription.userId;

const cadenceDescription = (cadence: SubscriptionCadence) => {
    switch (cadence) {
        case SubscriptionCadence.Monthly:
            return "The selected users will receive one automatic KBo Flow email each month.";
        case SubscriptionCadence.Daily:
            return "The selected users will receive one automatic KBo Flow email each day.";
        default:
            return "The selected users will receive one automatic KBo Flow email each week.";
    }
};

export const SubscriptionsPage = observer(function SubscriptionsPage({
    organizationId,
    breadcrumbs,
}: Props) {
    const authStore = useStore(AuthStore);
    const messageStore = useStore(MessageStore);
    const subscriptionService = React.useMemo(
        () => new KboFlowSubscriptionsService(),
        [],
    );
    const userService = React.useMemo(() => new UserService(), []);
    const resolvedOrganizationId =
        organizationId ??
        authStore.orgStore?.selectedOrganization?.id ??
        authStore.effectiveOrgId;
    const canManage = authStore.isUserUltra();

    const dataGridStore = React.useMemo(() => {
        const store = new AcxDataGridStore("KboSubscriptions", "Kbo");
        store.checkboxSelection = false;
        store.disableRowSelectionOnClick = true;
        store.disableLocalConfig = true;
        store.hideVertIcon = true;
        store.pagination = true;
        store.paginationModel = { page: 0, pageSize: 10 };
        store.pageSizeOptions = [10, 25, 50];
        store.onPaginationModelChange = (model) => {
            store.paginationModel = model;
        };
        store.heightOverride = "420px";
        return store;
    }, []);

    const [subscriptions, setSubscriptions] = React.useState<
        KboFlowSubscription[]
    >([]);
    const [users, setUsers] = React.useState<UserForSharing[]>([]);
    const [loadError, setLoadError] = React.useState<string>();
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editing, setEditing] = React.useState<KboFlowSubscription>();
    const [selectedUsers, setSelectedUsers] = React.useState<UserOption[]>([]);
    const [cadence, setCadence] = React.useState(SubscriptionCadence.Weekly);
    const [saving, setSaving] = React.useState(false);
    const [deleting, setDeleting] = React.useState<KboFlowSubscription>();
    const [deleteLoading, setDeleteLoading] = React.useState(false);

    const toRows = React.useCallback(
        (items: KboFlowSubscription[]): SubscriptionRow[] =>
            items.map((subscription) => ({
                ...subscription,
                displayName: getDisplayName(subscription),
                cadenceLabel:
                    cadenceLabels[subscription.cadence] ??
                    String(subscription.cadence),
            })),
        [],
    );

    const loadSubscriptions = React.useCallback(
        async (abortSignal?: AbortSignal) => {
            dataGridStore.setLoading(true);
            setLoadError(undefined);
            try {
                const result = await subscriptionService.list(abortSignal);
                setSubscriptions(result);
                dataGridStore.setRows(toRows(result));
            } catch (error) {
                if (!abortSignal?.aborted) {
                    setLoadError(
                        error instanceof Error
                            ? error.message
                            : "Subscriptions could not be loaded.",
                    );
                }
            } finally {
                if (!abortSignal?.aborted) dataGridStore.setLoading(false);
            }
        },
        [dataGridStore, subscriptionService, toRows],
    );

    React.useEffect(() => {
        const controller = new AbortController();
        void loadSubscriptions(controller.signal);
        return () => controller.abort();
    }, [loadSubscriptions]);

    React.useEffect(() => {
        if (!canManage || !resolvedOrganizationId) return undefined;

        let cancelled = false;
        userService
            .getUsersForSharing(resolvedOrganizationId)
            .then((result) => {
                if (!cancelled) setUsers(result);
            })
            .catch(() => {
                if (!cancelled) setUsers([]);
            });

        return () => {
            cancelled = true;
        };
    }, [canManage, resolvedOrganizationId, userService]);

    const eligibleUsers = React.useMemo(() => {
        const enrolledUserIds = new Set(
            subscriptions.map((subscription) => subscription.userId),
        );
        return users
            .filter((user) => !enrolledUserIds.has(user.userId))
            .map((user) => ({
                ...user,
                label: user.email || user.userName,
            }));
    }, [subscriptions, users]);

    const closeDialog = React.useCallback(() => {
        if (saving) return;
        setDialogOpen(false);
        setEditing(undefined);
        setSelectedUsers([]);
        setCadence(SubscriptionCadence.Weekly);
    }, [saving]);

    const openCreate = React.useCallback(() => {
        setEditing(undefined);
        setSelectedUsers([]);
        setCadence(SubscriptionCadence.Weekly);
        setDialogOpen(true);
    }, []);

    const openEdit = React.useCallback((subscription: KboFlowSubscription) => {
        setEditing(subscription);
        setSelectedUsers([]);
        setCadence(subscription.cadence);
        setDialogOpen(true);
    }, []);

    React.useEffect(() => {
        const columns: IColDef[] = [
            {
                field: "displayName",
                headerName: "User Name",
                flex: 1,
                minWidth: 180,
                renderCell: (params: GridRenderCellParams<SubscriptionRow>) => (
                    <Typography component="span" fontWeight={700}>
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "email",
                headerName: "User Email",
                flex: 1.2,
                minWidth: 220,
            },
            {
                field: "cadenceLabel",
                headerName: "Cadence",
                width: 120,
                renderCell: (params: GridRenderCellParams<SubscriptionRow>) => (
                    <AcxChip
                        size="small"
                        color={
                            params.row.cadence === SubscriptionCadence.Monthly
                                ? "green"
                                : "blue"
                        }
                        label={params.value}
                    />
                ),
            },
            {
                field: "lastRun",
                headerName: "Last Run",
                width: 190,
                renderCell: (params: GridRenderCellParams<SubscriptionRow>) =>
                    formatDateTime(params.row.lastRun),
            },
            {
                field: "nextRun",
                headerName: "Next Run",
                width: 190,
                renderCell: (params: GridRenderCellParams<SubscriptionRow>) =>
                    formatDateTime(params.row.nextRun),
            },
        ];

        if (canManage) {
            columns.push({
                field: "actions",
                headerName: "",
                width: 190,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<SubscriptionRow>) => (
                    <Stack
                        direction="row"
                        columnGap={1}
                        alignItems="center"
                        height="100%"
                    >
                        <AcxButton
                            size="small"
                            color="white"
                            startIcon={<EditOutlined />}
                            onClick={() => openEdit(params.row)}
                        >
                            Edit
                        </AcxButton>
                        <AcxButton
                            size="small"
                            color="white"
                            startIcon={<DeleteOutlineRounded />}
                            onClick={() => setDeleting(params.row)}
                        >
                            Delete
                        </AcxButton>
                    </Stack>
                ),
            });
        }

        void dataGridStore.setColumns(columns, true);
    }, [canManage, dataGridStore, openEdit]);

    const saveSubscription = async () => {
        if (!editing && selectedUsers.length === 0) return;

        setSaving(true);
        try {
            if (editing) {
                await subscriptionService.update(editing.id, {
                    cadence,
                    workflowName: editing.workflowName,
                    workflowInput: editing.workflowInput,
                });
                messageStore.logMessage("Subscription updated.", "success");
            } else {
                await subscriptionService.createBatch({
                    userIds: selectedUsers.map((user) => user.userId),
                    cadence,
                });
                messageStore.logMessage("Subscriptions added.", "success");
            }

            setDialogOpen(false);
            setEditing(undefined);
            setSelectedUsers([]);
            await loadSubscriptions();
        } catch (error) {
            messageStore.logError(
                error instanceof Error
                    ? error.message
                    : "The subscription could not be saved.",
            );
        } finally {
            setSaving(false);
        }
    };

    const deleteSubscription = async () => {
        if (!deleting) return;

        setDeleteLoading(true);
        try {
            await subscriptionService.remove(deleting.id);
            messageStore.logMessage("Subscription deleted.", "success");
            setDeleting(undefined);
            await loadSubscriptions();
        } catch (error) {
            messageStore.logError(
                error instanceof Error
                    ? error.message
                    : "The subscription could not be deleted.",
            );
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <Stack
            rowGap={2}
            padding={3}
            height="100%"
            width="100%"
            sx={{ overflow: "hidden" }}
        >
            {breadcrumbs}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                rowGap={1}
                flexShrink={0}
            >
                <Stack rowGap={0.5}>
                    <Typography variant="h5" fontWeight={600}>
                        KBo Flow Subscriptions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Enroll tenant users in recurring KBo Flow research
                        emails. Runs begin at 8:00 AM Eastern.
                    </Typography>
                </Stack>
                {canManage && (
                    <AcxButton
                        color="primary"
                        startIcon={<AddRounded />}
                        onClick={openCreate}
                        disabled={eligibleUsers.length === 0}
                        disabledTooltip={
                            eligibleUsers.length === 0
                                ? "Every eligible tenant user is already subscribed."
                                : undefined
                        }
                    >
                        Add Subscription
                    </AcxButton>
                )}
            </Stack>

            {loadError && <Alert severity="error">{loadError}</Alert>}

            <Box height="100%">
                <AcxDataGrid dataGridStore={dataGridStore} />
            </Box>

            <AcxDialog
                isOpen={dialogOpen}
                onClose={closeDialog}
                title={editing ? "Edit Subscription" : "Add Subscription"}
                subTitle={
                    editing
                        ? "Update the cadence for this recurring KBo Flow research email."
                        : "Enroll people in recurring KBo Flow research emails."
                }
                maxWidth="sm"
                fullWidth
                buttonsDisabled={saving}
                dialogContentChildren={
                    <Stack rowGap={3} paddingTop={1}>
                        {editing ? (
                            <Stack rowGap={0.5}>
                                <Typography fontWeight={600}>
                                    Recipient
                                </Typography>
                                <Typography>
                                    {getDisplayName(editing)}
                                </Typography>
                                {editing.email && (
                                    <Typography color="text.secondary">
                                        {editing.email}
                                    </Typography>
                                )}
                            </Stack>
                        ) : (
                            <AcxSelectMulti<UserOption>
                                id="kbo-subscription-recipients"
                                inputLabel="Recipients"
                                placeholder="Select tenant users by email"
                                options={eligibleUsers}
                                defaultValue={selectedUsers}
                                onChange={(options) =>
                                    setSelectedUsers(options ?? [])
                                }
                                valueField="userId"
                                labelField="label"
                                fullWidth
                                isClearable
                                containerHeight="auto"
                                maxContainerHeight="96px"
                                alignControls="flex-start"
                            />
                        )}
                        <AcxToggleButton
                            id="kbo-subscription-cadence"
                            inputLabel="Cadence"
                            defaultValue={cadence}
                            exclusive
                            items={cadenceOptions}
                            onChange={(value) => {
                                if (value !== null) {
                                    setCadence(value as SubscriptionCadence);
                                }
                            }}
                            groupFlexGrow={1}
                        />
                        <Paper
                            variant="outlined"
                            sx={(theme) => ({
                                padding: theme.spacing(2),
                                backgroundColor: theme.palette.gray[50],
                            })}
                        >
                            <Typography color="text.secondary">
                                {cadenceDescription(cadence)}
                            </Typography>
                        </Paper>
                    </Stack>
                }
            >
                <AcxButton
                    color="primary"
                    id="save-kbo-subscription"
                    onClick={saveSubscription}
                    loading={saving}
                    disabled={
                        saving || (!editing && selectedUsers.length === 0)
                    }
                >
                    {editing ? "Save" : "Add Subscription"}
                </AcxButton>
            </AcxDialog>

            <AcxConfirmationDialog
                isOpen={Boolean(deleting)}
                onClose={() => {
                    if (!deleteLoading) setDeleting(undefined);
                }}
                title="Delete Subscription"
                content={`Stop recurring KBo Flow emails for ${
                    deleting ? getDisplayName(deleting) : "this user"
                }?`}
                confirmButtonText="Delete Subscription"
                confirmButtonId="confirm-delete-kbo-subscription"
                onConfirm={deleteSubscription}
                isLoading={deleteLoading}
            />
        </Stack>
    );
});

export default SubscriptionsPage;
